import { useState } from "react";
import { Link } from "react-router-dom";
import PageLayout from "@/components/layout/PageLayout";
import PageHero from "@/components/shared/PageHero";
import { usePageContent } from "@/hooks/usePageContent";
import { useSettings } from "@/context/SettingsContext";

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://cashmyproperty.ai/" },
    { "@type": "ListItem", position: 2, name: "FAQ", item: "https://cashmyproperty.ai/faq" },
  ],
};

const categories = [
  {
    label: "Ownership & Legal",
    icon: "ri-scales-3-line",
    faqs: [
      { q: "Will I lose ownership of my land?", a: "Absolutely not. All our strategies are designed to retain your ownership. Whether it's a JDA, long-term lease, or franchise placement — your name stays on the title deed throughout. We monetize your land, not your land rights." },
      { q: "How is revenue shared in a Joint Development Agreement (JDA)?", a: "In a JDA, you contribute land and the developer funds construction. Typically landowners receive 35–45% of the developed built-up area or its equivalent cash value. Our team negotiates to maximize your share based on land value, location, and developer interest." },
      { q: "What are the risks of development on my land?", a: "We conduct thorough due diligence on all development partners. Our legal team reviews agreements and structures deals with milestone-based payments, strong exit clauses, and dispute resolution mechanisms. You're protected at every stage." },
      { q: "Do I need to clear legal disputes before applying?", a: "Not necessarily. We can work with disputed land, ancestral land, or joint ownership situations — however, a clear title is required before any deal closure. We guide you through the legal cleanup process if needed." },
    ],
  },
  {
    label: "Process & Timeline",
    icon: "ri-time-line",
    faqs: [
      { q: "How long does the entire process take?", a: "The AI Feasibility Report is delivered in 48 hours. Partner matching typically takes 2–6 weeks. Deal structuring and signing takes 4–8 weeks. For leases, income usually begins within 3–6 months. JDA construction timelines range from 12–24 months." },
      { q: "How does leasing work? Do I need to build anything?", a: "In most lease cases, the tenant (warehouse operator, QSR brand, etc.) builds their own structure on your land at their own cost. You simply provide the land and collect rent every month. Zero investment from your side." },
      { q: "What types of land do you work with?", a: "We work with agricultural land, residential plots, commercial plots, industrial land, highway-facing sites, and even disputed or ancestral land (subject to clear title). Minimum usable size: 2,000 sq.ft for retail/franchise, 10,000 sq.ft+ for warehousing and JDA." },
      { q: "How long does the process take?", a: "The AI report is delivered in 48 hours. Match identification: 2–6 weeks. Agreement signing: 4–8 weeks. Income start (for leases): 3–6 months. JDA construction: 12–24 months depending on project scale." },
    ],
  },
  {
    label: "AI & Reports",
    icon: "ri-brain-line",
    faqs: [
      { q: "How accurate is the AI Feasibility Report?", a: "Our HBU Engine draws on real-time data from zoning databases, infrastructure projects, footfall analytics, and live market comparables. Across 10,000+ acres analyzed, we achieve within 8–12% variance of realized values in most cases." },
      { q: "What exactly is included in the AI Feasibility Report?", a: "The 30–40 page report includes: HBU score breakdown, top 3 monetization strategies with ROI projections, brand/developer shortlist, comparable transaction data, 5-year value appreciation forecast, and legal/compliance summary — reviewed by our expert team." },
      { q: "Can the AI analyze land outside major cities?", a: "Yes. Our datasets cover Tier 1, Tier 2, Tier 3 cities and rural areas across 25+ Indian states. We have specific modules for highway corridors, agricultural zones, coastal regions, and hill stations." },
    ],
  },
  {
    label: "NRI & Remote Owners",
    icon: "ri-plane-line",
    faqs: [
      { q: "Do you work with NRIs managing land from abroad?", a: "Yes — NRIs are one of our core segments. We handle all ground-level coordination, documentation, and partner management on your behalf. Final agreements can be executed via registered Power of Attorney (POA) from abroad." },
      { q: "Can NRIs pay for the report from abroad?", a: "Yes. We accept international payments via credit/debit cards, PayPal, and wire transfer. NRIs can also use NRO/NRE accounts via NEFT/RTGS from Indian banks." },
      { q: "Is my land data secure?", a: "Yes. All land details are encrypted, stored on secure servers, and never shared with any third party without your explicit consent. We are compliant with India's data privacy guidelines." },
    ],
  },
  {
    label: "Pricing & Fees",
    icon: "ri-money-rupee-circle-line",
    faqs: [
      { q: "What does the feasibility report cost?", a: "The AI Feasibility Report is ₹4,999 per plot — a one-time fee. This covers full HBU analysis, 3 monetization options, 5-year projections, brand shortlist, and expert review." },
      { q: "When do I pay the success fee?", a: "Only after the deal is fully signed and the first payment from the partner is received. Not on verbal agreements, not on LOIs. Only on executed deals." },
      { q: "Is the feasibility report fee refundable?", a: "The ₹4,999 fee is non-refundable as it involves significant compute and expert time. However, if you proceed with a deal through us, this fee is adjusted against the success fee." },
    ],
  },
];

export default function FAQPage() {
  const { getContent } = usePageContent('faq');
  const { settings } = useSettings();
  const displayPhone = settings.general.primary_phone || '+91 90350 63455';
  const phoneRaw = settings.general.primary_phone_raw || '+919035063455';
  const footerHeading = getContent('faq-cta-heading') || 'Still have questions?';
  const footerSub = getContent('faq-cta-sub') || 'Our land strategy team is available to answer any specific questions about your property.';
  const [openKey, setOpenKey] = useState<string | null>(null);

  const toggle = (key: string) => setOpenKey(openKey === key ? null : key);

  return (
    <PageLayout>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>

      <PageHero
        badge="Help Center"
        title="Frequently Asked"
        highlight="Questions"
        subtitle="Everything you need to know about monetizing your land with CashMyProperty.ai."
        image="https://readdy.ai/api/search-image?query=Professional%20customer%20support%20and%20knowledge%20base%20concept%2C%20dark%20modern%20office%20setting%2C%20glowing%20screens%20with%20FAQ%20interface%2C%20teal%20and%20amber%20accent%20lighting%2C%20cinematic%20business%20photography%2C%20India%20professional%20environment&width=1800&height=800&seq=faq-hero-page&orientation=landscape"
      />

      {/* Breadcrumb */}
      <section className="py-4 bg-background-50 border-b border-primary-900/20">
        <div className="container mx-auto px-4 lg:px-8">
          <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm text-foreground-500">
            <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">
              <i className="ri-home-4-line mr-1" />
              Home
            </Link>
            <i className="ri-arrow-right-s-line text-xs" />
            <span className="text-foreground-950 font-medium">FAQ</span>
          </nav>
        </div>
      </section>

      {/* FAQs by category */}
      <section className="py-24 bg-background-50">
        <div className="container mx-auto px-4 lg:px-8 max-w-4xl">
          <div className="space-y-14">
            {categories.map((cat) => (
              <div key={cat.label}>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-background-50 border border-primary-500/30">
                    <i className={`${cat.icon} text-xl text-primary-500`} />
                  </div>
                  <h2 className="text-xl font-display font-bold text-foreground-950">{cat.label}</h2>
                </div>
                <div className="space-y-3">
                  {cat.faqs.map((faq, i) => {
                    const key = `${cat.label}-${i}`;
                    return (
                      <div
                        key={key}
                        className={`rounded-xl border transition-all duration-300 ${
                          openKey === key ? "bg-background-200 border-primary-700/40" : "card-dark"
                        }`}
                      >
                        <button
                          className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer"
                          onClick={() => toggle(key)}
                        >
                          <span className="font-display font-semibold text-foreground-950 pr-4">{faq.q}</span>
                          <i className={`ri-add-line text-primary-500 text-xl shrink-0 transition-transform duration-300 ${openKey === key ? "rotate-45" : ""}`} />
                        </button>
                        {openKey === key && (
                          <div className="px-6 pb-6 text-sm text-foreground-600 leading-relaxed">{faq.a}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Still have questions */}
      <section className="py-20 bg-background-100">
        <div className="container mx-auto px-4 lg:px-8 text-center max-w-xl">
          <h2 className="text-2xl md:text-3xl font-display font-bold text-foreground-950 mb-4">
            Still have questions?
          </h2>
          <p className="text-foreground-600 mb-8">
            Our land strategy team is available to answer any specific questions about your property.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap">
              <i className="ri-message-3-line" /> Chat with a Strategist
            </Link>
            <a href={`tel:${phoneRaw}`} className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-primary-700/50 text-primary-600 font-semibold hover:bg-primary-500/10 transition-colors whitespace-nowrap">
              <i className="ri-phone-line" /> {displayPhone}
            </a>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}